// CRM E-I: nach dem Wählen das Ergebnis in EINEM Klick festhalten.
//
// Keine Modal-Kette. Bei rund 30 Anrufen am Tag und Nutzer wird alles Aufwändigere
// nicht benutzt, und die Historie verrottet — dann ist das ganze CRM eine Karteileiche.
// Ein Ergebnis wählen, speichern, fertig; Notiz, Dauer und Folge-Aufgabe sind Zugaben,
// keine Stationen.
//
// Wo das Panel erscheint: im schwebenden `CallPanel`, das `ClickToDialButton` nach
// dem Wählen und `CallContactSelectModal` nach der Auswahl aufmacht. Es bringt
// deshalb weder Rahmen noch Knopfleiste mit — Speichern und Verwerfen stehen im Fuß
// des Fensters und rufen `save()` über die Ref auf.
//
// Richtungslos: `direction` entscheidet nur, was gespeichert wird. Ein eingehender
// Anruf läuft durch dieselbe Maske, es wird davor bloß nicht gewählt.
import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  useCallTimer,
  formatDuration,
  parseDuration,
  formatPhoneNumber,
  canonicalPhoneNumber,
  phoneDigits,
  numberIsKnown,
  findByNumber,
} from "../../helpers/telephony";
import { logActivity } from "../../helpers/activities";
import ActivityDocumentsField from "../ActivityDocumentsField";
import RichNoteField, { noteIsEmpty, noteValue } from "../RichNoteField";
import { saveActivityDocuments } from "../activityDocuments";
import {
  CALL_OUTCOMES_OFFERED,
  callOutcomeLabel,
  callOutcomeNotReached,
} from "../../constants/callOutcomes";
import { createTasks } from "../graphql/taskForm.queries";
import { updateCompanies } from "../company/graphql/locationCard.mutations";
import { updateCompaniesContactPersons } from "../company/graphql/companyContactModal.mutations";
import { client } from "../../helpers/amplifyClient";
import FollowUpTaskField from "../FollowUpTaskField";
import { calculateFollowUpDate } from "../taskDateHelpers";

// Die Ergebnis-Werte stehen in src/constants/callOutcomes.json — dieselbe Datei
// liest das Migrationsscript. Gespeichert wird der Code, angezeigt das uebersetzte
// Label; `group` ist reine Oberflaeche: das Feld mischt zwei Achsen (Verbindung und
// Qualifizierung), getrennt gelistet sind es beim Hinsehen 4+3 statt sieben aehnlich
// klingende Zeilen.

/**
 * @param dialResult  was `ClickToDialButton` beim Wählen geliefert hat
 * @param companyID   Firma der Aktivität
 * @param contactPersonId  Ansprechpartner, optional
 * @param task        Aufgabe, aus der der Anruf entstand — optional (gemessener
 *                    Regelfall: 53 % der Anrufe haben keine)
 * @param onSaved     Rückmeldung mit der angelegten Aktivität
 * @param onBusy      meldet, ob gerade gespeichert wird
 * @param onOutcomeChange  meldet das gewählte Ergebnis — der Speichern-Knopf des
 *                    Modals hängt daran
 * @param ref         `save()` löst das Speichern aus, `hasInput()` sagt, ob der
 *                    Nutzer schon etwas eingetragen hat
 */
const CallOutcomePanel = React.forwardRef((props, ref) => {
  const {
    dialResult,
    // "outbound" | "inbound" — eingehende Anrufe werden ueber dieselbe Maske
    // festgehalten, nur ohne Waehlen davor.
    direction = "outbound",
    companyID,
    contactPersonId,
    objectId,
    objectType,
    task,
    onSaved,
    userId,
    onBusy,
    onOutcomeChange,
    // Handgetippte Nummer: dann bietet das Panel an, sie zu hinterlegen.
    unknownNumber,
    company,
    contacts = [],
  } = props;
  const { t } = useTranslation(["components", "common"]);
  const timer = useCallTimer();
  const [note, setNote] = useState("");
  // Rohtext, solange in der Uhr getippt wird. null = nicht im Bearbeiten.
  const [timeDraft, setTimeDraft] = useState(null);
  // Lief der Zaehler, als das Tippen begann? Dann laeuft er danach weiter.
  const wasRunning = useRef(false);
  const [timeEdited, setTimeEdited] = useState(false);
  const [followUp, setFollowUp] = useState({
    enabled: false,
    amount: 3,
    unit: "business_days",
  });
  const [outcome, setOutcome] = useState("");
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  // Wer am Telefon war: "" = niemand ausgewaehlt, "company" = die Zentrale, sonst
  // die Id des Ansprechpartners. Vorbelegt mit dem, der beim Oeffnen feststand —
  // beim eingehenden Anruf ist das der ueber die Rufnummer gefundene Kontakt.
  const [who, setWho] = useState(
    contactPersonId ||
      // Kein Ansprechpartner zur Nummer, aber die Zentrale passt: dann war es die
      // Zentrale. Gilt in beide Richtungen — angerufen wie angenommen.
      (company?.id && findByNumber(dialResult?.number, [company]) ? "company" : "")
  );
  // Eine noch nirgends gespeicherte Nummer beim Ausgewaehlten hinterlegen.
  const [saveNumber, setSaveNumber] = useState(false);
  const [assignField, setAssignField] = useState("phone");
  const { user } = useSelector((state) => ({ user: state.Auth.user }));

  // Auswahl fuer "wer war dran": die Zentrale und alle Ansprechpartner der Firma.
  // Kommen keine mit (Waehlen aus einer einzelnen Kontaktzeile), gibt es nichts
  // auszuwaehlen — dann bleibt es beim mitgegebenen Kontakt.
  const whoOptions = [
    ...(company?.id
      ? [{ value: "company", label: company.name || t("Company"), record: company }]
      : []),
    ...contacts.map((c) => ({
      value: c.id,
      label:
        [c.firstName, c.lastName].filter(Boolean).join(" ") || t("Unnamed Contact"),
      record: c,
    })),
  ];
  const whoRecord = whoOptions.find((o) => o.value === who)?.record;
  // Der Anruf gehoert dem Ausgewaehlten; die Zentrale ist kein Ansprechpartner.
  const effectiveContactId = whoOptions.length
    ? who === "company"
      ? ""
      : who
    : contactPersonId;

  // Steht schon eine Nummer fest (gewaehlt oder eingegeben), gilt die — sonst kam
  // man ueber "Anruf nachtragen" ohne Nummer, und der Ausgewaehlte hat vielleicht
  // zwei: dann muss ausgewaehlt werden, mit welcher telefoniert wurde.
  const [numberKind, setNumberKind] = useState("");
  const numberKinds = ["phone", "mobil"].filter((k) => whoRecord?.[k]);
  const resolvedNumber =
    dialResult?.number || whoRecord?.[numberKind] || whoRecord?.[numberKinds[0]] || "";

  // Hinterlegen wird nur angeboten fuer eine Nummer, die noch nirgends steht
  // (numberIsKnown vergleicht ueber alle Schreibweisen) — und nur, wenn klar ist,
  // bei wem sie hinterlegt werden soll.
  const offerAssign =
    phoneDigits(unknownNumber).length >= 3 &&
    Boolean(whoRecord) &&
    !numberIsKnown(unknownNumber, whoOptions.map((o) => o.record));
  // Companies hat KEIN mobil-Feld (schema.graphql: nur `phone`), nur
  // CompaniesContactPersons hat beide. Die Auswahl entfaellt deshalb bei der Firma.
  const assignFields = who === "company" ? ["phone"] : ["phone", "mobil"];

  // Der Zähler startet mit dem Wählen und läuft, bis ein Ergebnis fällt. Er ist
  // wall-clock-verankert (useCallTimer), bleibt also auch in einem gedrosselten
  // Hintergrund-Tab richtig.
  useEffect(() => {
    timer.reset();
    timer.start();
    return () => timer.stop();
    // Genau einmal je Wählvorgang; timer-Funktionen sind stabil genug, und ein
    // Neustart bei jedem Render wäre ein Zähler, der nie zählt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialResult?.dialedAt]);

  // Das Modal darf sich nicht mitten im Speichern schließen lassen.
  useEffect(() => {
    if (typeof onBusy === "function") onBusy(Boolean(saving));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving]);

  // Der Zähler IST der Wert — eine Korrektur setzt ihn, sie legt keinen zweiten
  // Wert daneben. KEIN Pflichtfeld: HubSpot bekommt die Dauer trotz VOIP nur bei
  // etwa der Hälfte der Anrufe, ein Pflichtfeld erzwänge eine erfundene Zahl.
  const effectiveSeconds = timer.elapsedSeconds;
  const timeText = timeDraft === null ? formatDuration(effectiveSeconds) : timeDraft;

  const save = async () => {
    if (!outcome || saving) return;
    setSaving(true);
    timer.stop();
    try {
      // Erst die Dateien, dann der Datensatz: die Anhaenge stehen als Feld IN der
      // Aktivitaet (kein @hasMany), also muessen ihre IDs vor der Mutation feststehen.
      const documents = await saveActivityDocuments({
        docs,
        companyId: companyID,
        country: user?.country,
      });

      const activity = await logActivity({
        userId,
        task,
        // Niemanden erreicht: der Anruf haengt an der Aufgabe, aber der Auftrag ist
        // nicht erfuellt — die Aufgabe bleibt OFFEN und sammelt den naechsten Versuch.
        completesTask: !callOutcomeNotReached(outcome),
        fields: {
          activityType: "call",
          // Kein Betreff: er trug das Ergebnis als Text, also dieselbe Angabe zweimal
          // — und einmal gespeichert war sie in keiner Sprache mehr uebersetzbar.
          // `outcome` traegt den Code, die Karte uebersetzt ihn beim Anzeigen.
          body: noteValue(note),
          outcome,
          direction,
          occurredAt: new Date(dialResult?.dialedAt || Date.now()).toISOString(),
          // Nur wenn wirklich gezählt wurde — 0 wäre eine Behauptung, kein Messwert.
          ...(effectiveSeconds > 0 ? { durationSeconds: effectiveSeconds } : {}),
          companyID,
          contactPersonId: effectiveContactId,
          phoneNumber: resolvedNumber,
          ...(objectId
            ? { objectID: objectId, objectType: objectType || "CustomerRequest" }
            : task?.objectID
              ? { objectID: task.objectID, objectType: task.objectType }
              : {}),
          ...(documents.length > 0 ? { documents } : {}),
        },
      });

      if (offerAssign && saveNumber) {
        // Zugabe wie die Folge-Aufgabe: scheitert sie, ist der Anruf trotzdem
        // festgehalten — deshalb ein eigenes try.
        try {
          await client.graphql({
            query:
              who === "company" ? updateCompanies : updateCompaniesContactPersons,
            variables: {
              input: {
                id: who === "company" ? companyID : who,
                [assignField]: canonicalPhoneNumber(dialResult?.number) || dialResult?.number,
              },
            },
          });
          toast.success(t("Phone number saved."));
        } catch (error) {
          console.error("Nummer konnte nicht hinterlegt werden", error);
          toast.error(
            t("The call was logged, but the phone number was not saved.")
          );
        }
      }

      if (followUp.enabled) {
        // Die Folge-Aufgabe ist eine Zugabe: scheitert sie, ist das Ergebnis trotzdem
        // festgehalten. Deshalb ein eigenes try und kein gemeinsames.
        try {
          const dueDate = calculateFollowUpDate(
            followUp.amount,
            followUp.unit
          );
          await client.graphql({
            query: createTasks,
            variables: {
              input: {
                subject: t("Follow-up: {{subject}}", {
                  subject: callOutcomeLabel(outcome),
                }),
                taskType: "call",
                status: "open",
                priority: "medium",
                assigneeType: "user",
                // Der Anlass der Folge-Aufgabe: dieser Anruf. Die Gegenrichtung zu
                // Tasks.activityIDs — der Verlauf verlinkt hierueber zurueck.
                originActivityID: activity.id,
                dueDate: dueDate.toISOString(),
                ...(userId ? { assignedUserId: userId } : {}),
                ...(companyID ? { companyID } : {}),
                ...(effectiveContactId
                  ? { contactPersonId: effectiveContactId }
                  : {}),
                ...(objectId
                  ? { objectID: objectId, objectType: objectType || "CustomerRequest" }
                  : task?.objectID
                    ? { objectID: task.objectID, objectType: task.objectType }
                    : {}),
              },
            },
          });
          toast.success(t("Follow-up task created."));
        } catch (error) {
          console.error("Folge-Aufgabe fehlgeschlagen", error);
          toast.error(
            t("The call was logged, but the follow-up task was not created.")
          );
        }
      }

      toast.success(t("Call logged."));
      if (typeof onSaved === "function") onSaved(activity);
    } catch (error) {
      console.error("Anruf konnte nicht festgehalten werden", error);
      toast.error(t("Could not log the call."));
    } finally {
      setSaving(false);
    }
  };

  // `save()` gehört dem Modal-Fuß, `hasInput()` der Rückfrage vor dem Verwerfen —
  // sonst wäre eine halb getippte Notiz mit einem Klick weg.
  useImperativeHandle(ref, () => ({
    save,
    hasInput: () =>
      Boolean(
        outcome ||
          !noteIsEmpty(note) ||
          timeEdited ||
          followUp.enabled ||
          who !== (contactPersonId || "") ||
          docs.length
      ),
  }));

  return (
    <React.Fragment>
      {/* Wer am Telefon war. Eingehend ist das die eigentliche Frage: die Anlage
          liefert nur eine Rufnummer, und ob dahinter der Einkauf oder der Chef
          sass, weiss nur der, der gerade aufgelegt hat. Ausgehend steht die
          Antwort schon in der Auswahl davor und ist hier nur noch zu sehen. */}
      {whoOptions.length > 0 && (
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">
            {direction === "inbound" ? t("Who called?") : t("Who was on the phone?")}
          </Form.Label>
          <Form.Select
            value={who}
            disabled={saving}
            onChange={(e) => {
              const value = e.target.value;
              setWho(value);
              // Ein anderer Ausgewaehlter heisst ein anderer Datensatz — das
              // Hinterlegen faengt bei ihm wieder von vorn an.
              setSaveNumber(false);
              // Und, falls beide Nummern hat, auch eine neue Wahl, mit welcher
              // telefoniert wurde.
              setNumberKind("");
              const record = whoOptions.find((o) => o.value === value)?.record;
              // Vorbelegung auf das freie Feld — bei der Firma gibt es nur eines.
              setAssignField(
                value !== "company" && record?.phone ? "mobil" : "phone"
              );
            }}
          >
            <option value="">{t("Unknown")}</option>
            {whoOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {/* Nur wenn noch keine Nummer feststeht (nachgetragener Anruf ohne Waehlen)
          UND der Ausgewaehlte beide Felder besetzt hat — sonst ist es eindeutig
          oder es steht schon fest, mit welcher Nummer telefoniert wurde. */}
      {!dialResult?.number && numberKinds.length > 1 && (
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">{t("Which number?")}</Form.Label>
          <Form.Select
            value={numberKind || numberKinds[0]}
            disabled={saving}
            onChange={(e) => setNumberKind(e.target.value)}
          >
            {numberKinds.map((kind) => (
              <option key={kind} value={kind}>
                {(kind === "mobil" ? t("Mobile") : t("Phone")) +
                  " · " +
                  formatPhoneNumber(whoRecord[kind])}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {/* Eine Nummer, die noch nirgends steht, ist nur in diesem Fenster bekannt.
          Wer sie jetzt nicht hinterlegt, tippt sie beim naechsten Mal wieder —
          genau so veralten die Stammdaten. Deshalb steht das Angebot farbig und
          direkt an der Auswahl, zu der es gehoert. */}
      {offerAssign && (
        <div className="bg-warning-lighten border border-warning rounded p-2 mb-3">
          <Form.Check
            type="checkbox"
            id="callOutcomeSaveNumber"
            className="fw-bold"
            checked={saveNumber}
            disabled={saving}
            onChange={(e) => setSaveNumber(e.target.checked)}
            label={
              <span>
                <i className="mdi mdi-account-plus-outline font-18 me-1" />
                {t("Save {{number}} for {{name}}", {
                  number: formatPhoneNumber(unknownNumber),
                  name: whoOptions.find((o) => o.value === who)?.label,
                })}
              </span>
            }
          />
          {saveNumber && assignFields.length > 1 && (
            <Form.Select
              value={assignField}
              disabled={saving}
              className="mt-2"
              onChange={(e) => setAssignField(e.target.value)}
            >
              {assignFields.map((field) => {
                const label = field === "phone" ? t("Phone") : t("Mobile");
                const current = whoRecord?.[field];
                return (
                  <option key={field} value={field}>
                    {current
                      ? t("{{field}} (replaces {{number}})", {
                          field: label,
                          number: formatPhoneNumber(current),
                        })
                      : label}
                  </option>
                );
              })}
            </Form.Select>
          )}
          {/* Bei der Firma gibt es nur ein Feld, also auch keine Auswahl, die den
              alten Wert zeigen koennte — hier sagt es der Hinweis darunter. */}
          {saveNumber && assignFields.length === 1 && (
            <div className="font-12 mt-1">
              {whoRecord?.phone
                ? t("Replaces the current number {{number}}", {
                    number: formatPhoneNumber(whoRecord.phone),
                  })
                : t("No number stored yet")}
            </div>
          )}
        </div>
      )}

      {/* Kopf: die laufende Zeit ist die einzige Angabe, die nur JETZT stimmt —
          deshalb steht sie groß und nicht in einem zweiten Eingabefeld. Man tippt
          direkt hinein; das hält den Zähler an, damit die Korrektur nicht unter den
          Fingern weiterläuft. Der Knopf daneben lässt ihn wieder laufen: ein Anruf,
          der in Warteschleife oder Rückfrage hängt, soll nicht als Gesprächsdauer
          gezählt werden. */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="text-muted font-12 text-uppercase">
            <span className={"me-1 " + (timer.running ? "text-danger" : "text-muted")}>
              ●
            </span>
            {timer.running ? t("Call in progress") : t("Timer paused")}
          </div>
          <div className="d-flex align-items-center mt-1">
            <Form.Control
              plaintext
              className="font-24 font-monospace fw-bold lh-1 p-0 w-auto"
              /* size statt CSS-Breite: das Feld ist so breit wie sein Text, sonst
                 steht es auf 20 Zeichen und schiebt den Knopf nach rechts weg
                 (die Utility w-auto trägt !important und schlägt jede width). */
              htmlSize={Math.max(timeText.length, 4)}
              title={t("Click to edit the duration")}
              value={timeText}
              onFocus={() => {
                wasRunning.current = timer.running;
                timer.stop();
                setTimeDraft(formatDuration(effectiveSeconds));
              }}
              onChange={(e) => setTimeDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
              onBlur={() => {
                const parsed = parseDuration(timeDraft);
                // Unlesbares bleibt folgenlos: dann zählt weiter, was gemessen wurde.
                if (parsed !== null) {
                  timer.set(parsed);
                  setTimeEdited(true);
                }
                setTimeDraft(null);
                if (wasRunning.current) timer.start();
              }}
            />
            <Button
              variant="light"
              size="sm"
              className="ms-1 py-0 px-1 lh-1"
              title={timer.running ? t("Pause timer") : t("Resume timer")}
              onClick={() => (timer.running ? timer.stop() : timer.start())}
            >
              <i
                className={
                  "mdi font-18 " + (timer.running ? "mdi-pause" : "mdi-play")
                }
              />
            </Button>
          </div>
        </div>
        <div className="text-end">
          <div className="text-muted font-12 text-uppercase">{t("Phone")}</div>
          {/* Angezeigt lesbar, gespeichert roh (siehe phoneNumber oben). */}
          <div className="mt-1">{formatPhoneNumber(resolvedNumber)}</div>
        </div>
      </div>

      {/* Ein Feld statt sieben Knöpfe. Die zwei Gruppen trennen die beiden Achsen, die
          HubSpot in diesem einen Feld vermischt — erreicht (was der Kunde sagt) gegen
          nicht erreicht (was die Leitung macht). */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-bold">
          {t("How did the call go?")} <span className="text-danger">*</span>
        </Form.Label>
        <Form.Select
          value={outcome}
          disabled={saving}
          onChange={(e) => {
            setOutcome(e.target.value);
            if (typeof onOutcomeChange === "function")
              onOutcomeChange(e.target.value);
          }}
        >
          <option value="">{t("Select a result…")}</option>
          {["reached", "notReached"].map((group) => (
            <optgroup
              key={group}
              label={group === "reached" ? t("Reached") : t("Not reached")}
            >
              {CALL_OUTCOMES_OFFERED.filter((o) => o.group === group).map((o) => (
                /* Wert = Code, Anzeige = übersetztes Label. Fehlt ein Schlüssel,
                   liefert i18next den deutschen Originaltext. */
                <option key={o.code} value={o.code}>
                  {t(o.label)}
                </option>
              ))}
            </optgroup>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="text-muted font-12 text-uppercase mb-1">
          {t("Note")}
        </Form.Label>
        <RichNoteField
          rows={3}
          placeholder={t("Note (optional)")}
          value={note}
          onChange={setNote}
        />
      </Form.Group>

      <ActivityDocumentsField docs={docs} setDocs={setDocs} companyId={companyID} />

      <FollowUpTaskField
        value={followUp}
        onChange={setFollowUp}
        id="call-follow-up-task-checkbox"
      />
    </React.Fragment>
  );
});

export default CallOutcomePanel;
